
import { useState, useEffect, useCallback, useMemo } from 'react';
import Header from './components/Header';
import ProductGrid from './components/ProductGrid';
import Cart from './components/Cart';
import Footer from './components/Footer';
import { products as initialProducts } from '../../mocks/products';
import CookieBanner from '../../components/CookieBanner';

interface Product {
  id: string;
  name: string;
  price: number;
  image: string;
  category: string;
  description: string;
  stock: number;
  rating: number;
  reviews: number;
  verkaufseinheit?: string;
  mindestmengeStueck?: number;
  isB2B?: boolean;
  // NEUE FELDER für CSV-Update
  preis_pro_verpackungseinheit?: number;
  stueck_pro_ve?: number;
  price_pro_stueck?: number;
  ve_pro_karton?: number;
  karton_pro_palette?: number;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

export default function Shop() {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Startet mit true
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loadingError, setLoadingError] = useState<string>('');
  const [dataSource, setDataSource] = useState<'mock' | 'cache' | 'supabase'>('mock');
  
  // NEUE SUCHFUNKTION
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  
  // NEUES WILLKOMMENS-POPUP
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  
  // PAGINIERUNG STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('alle');

  // Popup beim ersten Besuch anzeigen
  useEffect(() => {
    const hasVisited = localStorage.getItem('snaeckomat-visited');
    if (!hasVisited) {
      setShowWelcomePopup(true);
      localStorage.setItem('snaeckomat-visited', 'true');
    }
  }, []);

  // Load cart from storage and listen for clearing events
  useEffect(() => {
    loadCartFromStorage();
    
    // Event Listener für Warenkorb-Clearing
    const handleCartCleared = () => {
      setCartItems([]);
    };
    
    window.addEventListener('cartCleared', handleCartCleared);
    
    return () => {
      window.removeEventListener('cartCleared', handleCartCleared);
    };
  }, []);

  const loadCartFromStorage = () => {
    try {
      const savedCart = localStorage.getItem('cartItems');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        setCartItems(parsedCart);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Warenkorbs:', error);
      localStorage.removeItem('cartItems'); // Beschädigte Daten entfernen
    }
  };

  const saveCartToStorage = (items: any[]) => {
    try {
      localStorage.setItem('cartItems', JSON.stringify(items));
    } catch (error) {
      console.error('Fehler beim Speichern des Warenkorbs:', error);
    }
  };

  const addToCart = (product: Product, quantity: number = 1) => {
    const newCartItems = [...cartItems];
    const existingItemIndex = newCartItems.findIndex(item => item.id === product.id);

    if (existingItemIndex > -1) {
      // KORRIGIERT: Menge wird zur bestehenden Menge hinzugefügt
      newCartItems[existingItemIndex].quantity += quantity;
    } else {
      const cartItem = {
        id: product.id,
        name: product.name,
        // KORRIGIERTE PREISZUWEISUNG - Verwendet den korrekten B2B-Preis
        price: product.preis_pro_verpackungseinheit || product.price || 0,
        preis_pro_verpackungseinheit: product.preis_pro_verpackungseinheit || product.price || 0,
        quantity: quantity, // KORRIGIERT: Verwendet die übergebene Menge
        image: product.image || '/placeholder-product.jpg',
        verkaufseinheit: product.verkaufseinheit,
        mindestmengeStueck: product.mindestmengeStueck,
        isB2B: product.isB2B,
        category: product.category,
        stueck_pro_ve: product.stueck_pro_ve,
        stueckProVE: product.stueck_pro_ve
      };
      newCartItems.push(cartItem);
    }

    setCartItems(newCartItems);
    saveCartToStorage(newCartItems); // Warenkorb persistent speichern
    // showAddedToCart handling omitted as not defined in original
  };

  const removeFromCart = (productId: string) => {
    const newCartItems = cartItems.filter(item => item.id !== productId);
    setCartItems(newCartItems);
    saveCartToStorage(newCartItems); // Änderungen speichern
  };

  const updateCartQuantity = (productId: string, quantity: number) => {
    // KORRIGIERT: Menge wird immer aktualisiert, auch wenn sie 0 ist
    // Nur bei negativen Werten wird das Produkt entfernt
    if (quantity < 0) {
      removeFromCart(productId);
      return;
    }

    const newCartItems = cartItems.map(item =>
      item.id === productId ? { ...item, quantity } : item
    );
    setCartItems(newCartItems);
    saveCartToStorage(newCartItems); // Änderungen speichern
  };

  const saveToCache = (products: Product[]) => {
    try {
      const lightProducts = products.map(p => ({
        id: p.id,
        name: p.name,
        price: p.price,
        image: p.image,
        category: p.category,
        stock: p.stock,
        isB2J: p.isB2J
      }));

      localStorage.setItem('shop-products-cache', JSON.stringify(lightProducts));
      localStorage.setItem('shop-cache-time', Date.now().toString());
    } catch (error) {
      // Cache-Fehler ignorieren
    }
  };

  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem('shop-products-cache');
      const cacheTime = localStorage.getItem('shop-cache-time');
      
      if (cached && cacheTime) {
        const age = Date.now() - parseInt(cacheTime);
        if (age < 5 * 60 * 1000) {
          const cachedProducts = JSON.parse(cached);
          if (Array.isArray(cachedProducts) && cachedProducts.length > 0) {
            return cachedProducts;
          }
        }
      }
    } catch (error) {
      // Cache-Fehler ignorieren
    }
    return null;
  };

  // KORRIGIERTE SUPABASE-FUNKTION - Verwendet die NEUE verbesserte Edge Function
  const loadProductsFromSupabase = async (page: number = 1, category: string = 'alle', search: string = '') => {
    if (!SUPABASE_URL) {
      setDataSource('mock');
      return;
    }

    try {
      setIsLoading(true);
      setLoadingError('');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // Längeres Timeout
      
      const params = new URLSearchParams({
        ...(category !== 'alle' && { category }),
        ...(search && { search })
      });
      
      // ✅ NEUE VERBESSERTE EDGE FUNCTION
      const response = await fetch(`${SUPABASE_URL}/functions/v1/produkte-123-api-kategorie-fix-v2?${params}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.products && Array.isArray(data.products)) {
        // ✅ KORREKTE DATENKONVERTIERUNG für B2B-Shop
        const convertedProducts = data.products.map((product: any) => ({
          id: product.id || `product-${Math.random()}`,
          name: product.Name || product.name || 'Unbekanntes Produkt',
          price: product["Preis pro Verpackungseinheit"] || product.price || 0,
          image: product.URL || product.image || 'https://via.placeholder.com/400x300?text=Kein+Bild',
          category: product.category || 'snacks',
          description: product.description || `${product.Name || 'Produkt'} - B2B Grosshandel`,
          stock: product.stock || 100, // Standard-Lagerbestand
          rating: 4.5,
          reviews: 12,
          isB2B: true,
          // ✅ B2B-SPEZIFISCHE FELDER
          preis_pro_verpackungseinheit: product["Preis pro Verpackungseinheit"] || product.price || 0,
          stueck_pro_ve: product.stueck_pro_ve || 1,
          price_pro_stueck: product.price_pro_stueck || 0,
          ve_pro_karton: product.ve_pro_karton || null,
          karton_pro_palette: product.karton_pro_palette || null,
          verkaufseinheit: 'VE'
        }));

        setProducts(convertedProducts);
        setTotalCount(data.actualCount || convertedProducts.length);
        setCurrentPage(1);
        setTotalPages(1);
        setHasNextPage(false);
        setHasPrevPage(false);
        setDataSource('supabase');
        
        if (category === 'alle' && !search) {
          saveToCache(convertedProducts);
        }
        
        console.log(`✅ Supabase-Produkte geladen: ${convertedProducts.length} von ${data.actualCount || convertedProducts.length} Produkten`);
        
        // 🏷️ KATEGORIE-STATISTIK ANZEIGEN
        const categoryStats = {};
        convertedProducts.forEach(product => {
          const cat = product.category || 'unbekannt';
          categoryStats[cat] = (categoryStats[cat] || 0) + 1;
        });
        
        console.log('📊 KATEGORIE-VERTEILUNG:');
        Object.entries(categoryStats).forEach(([category, count]) => {
          console.log(`   ${category}: ${count} Produkte`);
        });
        
      } else {
        throw new Error('Keine gültigen Produktdaten erhalten');
      }
    } catch (error: any) {
      console.warn('Supabase-Ladung fehlgeschlagen:', error.message);
      setLoadingError(error.message);
      
      // FALLBACK: Lokale Suche in Mock-Daten
      let filteredProducts = initialProducts;
      
      if (search && search.length >= 2) {
        const query = search.toLowerCase();
        filteredProducts = initialProducts.filter(product => 
          product.name.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query) ||
          product.description?.toLowerCase().includes(query)
        );
      }
      
      if (category !== 'alle') {
        filteredProducts = filteredProducts.filter(product => {
          const productCategory = product.category?.toLowerCase() || '';
          return productCategory === category.toLowerCase();
        });
      }
      
      setProducts(filteredProducts);
      setTotalCount(filteredProducts.length);
      setDataSource('mock');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = useCallback((newPage: number) => {
    return;
  }, []);

  const handleCategoryChange = useCallback((category: string) => {
    if (category !== selectedCategory) {
      setSelectedCategory(category);
      setSearchQuery(''); // Reset search when changing category
      loadProductsFromSupabase(1, category, '');
    }
  }, [selectedCategory]);

  // NEUE SUCHFUNKTION - Verbessert mit sofortiger lokaler Suche
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    
    // Sofortige lokale Suche für bessere UX
    if (query.length >= 2) {
      const searchQuery = query.toLowerCase();
      let filteredProducts = initialProducts.filter(product => 
        product.name.toLowerCase().includes(searchQuery) ||
        product.category?.toLowerCase().includes(searchQuery) ||
        product.description?.toLowerCase().includes(searchQuery)
      );
      
      // Auch Kategorie-Filter anwenden
      if (selectedCategory !== 'alle') {
        filteredProducts = filteredProducts.filter(product => {
          const productCategory = product.category?.toLowerCase() || '';
          return productCategory === selectedCategory.toLowerCase();
        });
      }
      
      setProducts(filteredProducts);
      setDataSource('mock');
      
      // Dann versuche Supabase-Suche im Hintergrund
      setTimeout(() => {
        loadProductsFromSupabase(1, selectedCategory, query);
      }, 300);
    } else if (query.length === 0) {
      // Reset zur normalen Ansicht
      loadProductsFromSupabase(1, selectedCategory, '');
    }
  }, [selectedCategory]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    loadProductsFromSupabase(1, selectedCategory, '');
  }, [selectedCategory]);

  // Modified initial loading effect
  useEffect(() => {
    // KEINE DEMO-PRODUKTE MEHR BEIM START
    // setProducts(initialProducts); // ❌ ENTFERNT
    // setDataSource('mock');        // ❌ ENTFERNT
    
    // Direkt mit Supabase-Loading starten
    setIsLoading(true);
    
    const cachedProducts = loadFromCache();
    if (cachedProducts && cachedProducts.length > 0) {
      setProducts(cachedProducts);
      setDataSource('cache');
      setIsLoading(false); // Cache gefunden, Loading beenden
    }
    
    // Supabase-Daten laden (auch wenn Cache vorhanden)
    setTimeout(() => {
      loadProductsFromSupabase(1, 'alle');
    }, cachedProducts ? 100 : 50); // Kurze Verzögerung wenn Cache vorhanden
  }, []);

  // Optimierte Berechnungen - KORRIGIERTE WARENKORB-BERECHNUNG
  const cartStats = useMemo(() => {
    const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);
    
    // KORRIGIERTE BERECHNUNG - Verwendet immer den B2B-Preis
    const totalPrice = cartItems.reduce((total, item) => {
      // Verwende den B2B-Preis (preis_pro_verpackungseinheit) als primären Preis
      const itemPrice = item.preis_pro_verpackungseinheit || item.price || 0;
      return total + (itemPrice * item.quantity);
    }, 0);
    
    return { totalItems, totalPrice };
  }, [cartItems]);

  // Status-Anzeige
  const getStatusDisplay = () => {
    if (isLoading) {
      return (
        <div className="flex items-center space-x-2 text-blue-600">
          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Lädt...</span>
        </div>
      );
    }

    switch (dataSource) {
      case 'supabase':
        return (
          <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-3 py-1 rounded-full">
            <i className="ri-cloud-line"></i>
            <span className="text-sm">Live-Daten</span>
          </div>
        );
      case 'cache':
        return (
          <div className="flex items-center space-x-2 text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
            <i className="ri-database-line"></i>
            <span className="text-sm">Cache</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-2 text-gray-600 bg-gray-50 px-3 py-1 rounded-full">
            <i className="ri-file-line"></i>
            <span className="text-sm">Demo-Daten</span>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        cartItemsCount={cartStats.totalItems}
        onCartClick={() => setIsCartOpen(true)}
      />
      
      <main className="pt-14 sm:pt-16 lg:pt-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4 lg:py-8">
          {/* VEREINFACHTER HERO-BEREICH - Mobile optimiert */}
          <div className="text-center mb-6 sm:mb-8 lg:mb-12">
            <div className="mb-4 sm:mb-6 lg:mb-8">
              <h1 className="font-['Pacifico'] text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-orange-500 drop-shadow-lg mb-3 sm:mb-4">
                Dä Snäckomat
              </h1>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
                B2B <span className="text-cyan-600">Grosshandel</span>
              </h2>
            </div>

            {/* NEUE SUCHFUNKTION - Mobile optimiert */}
            <div className="max-w-2xl mx-auto mb-4 sm:mb-6 px-2 sm:px-4">
              <div className={`relative transition-all duration-300 ${isSearchFocused ? 'transform scale-105' : ''}`}>
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <i className="ri-search-line text-gray-400 text-lg sm:text-xl"></i>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  placeholder="Produkte suchen... (z.B. M&M, Getränke, Alkohol)"
                  className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-full focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-100 bg-white shadow-lg transition-all duration-300"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center cursor-pointer hover:text-orange-500 transition-colors"
                  >
                    <i className="ri-close-line text-gray-400 text-lg sm:text-xl"></i>
                  </button>
                )}
              </div>
              
              {/* Suchvorschläge - Mobile optimiert */}
              <div className="flex flex-wrap justify-center gap-1 sm:gap-2 mt-3 sm:mt-4">
                {['M&M', 'Getränke', 'Alkohol', 'Schokolade', 'Energy Drink', 'Protein'].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => handleSearchChange(suggestion)}
                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-200 rounded-full hover:border-orange-500 hover:text-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Status - Mobile optimiert */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-3 sm:mb-4">
              {getStatusDisplay()}
              
              {loadingError && (
                <button
                  onClick={() => loadProductsFromSupabase(currentPage, selectedCategory, searchQuery)}
                  className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer px-2 py-1"
                >
                  Erneut versuchen
                </button>
              )}
            </div>
          </div>

          {/* Produktraster */}
          <ProductGrid 
            products={products}
            onAddToCart={addToCart}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            searchQuery={searchQuery}
          />

        </div>
      </main>

      <Footer />

      {/* Warenkorb */}
      {isCartOpen && (
        <Cart 
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          items={cartItems}
          onRemoveItem={removeFromCart}
          onUpdateQuantity={updateCartQuantity}
          totalPrice={cartStats.totalPrice}
        />
      )}

      {/* NEUES WILLKOMMENS-POPUP */}
      {showWelcomePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
            {/* Header mit Gradient */}
            <div className="bg-gradient-to-r from-orange-500 to-cyan-500 p-6 text-white text-center relative">
              <button
                onClick={() => setShowWelcomePopup(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-white"></i>
              </button>
              <div className="mb-4">
                <h1 className="font-['Pacifico'] text-2xl sm:text-3xl text-white drop-shadow-lg">
                  Dä Snäckomat Plüss
                </h1>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold">
                Hallo! 👋
              </h2>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Willkommensnachricht */}
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-3-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  Viel Spass beim Stöbern!
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Entdecken Sie unsere große Auswahl an Snacks und Getränken für Ihren Automaten.
                </p>
              </div>

              {/* Highlights */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-star-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Große Auswahl</h4>
                    <p className="text-sm text-gray-600">Über 1000 verschiedene Produkte</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-truck-line text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Schnelle Lieferung</h4>
                    <p className="text-sm text-gray-600">Direkt zu Ihrem Standort</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-shopping-bag-line text-purple-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Einfach bestellen</h4>
                    <p className="text-sm text-gray-600">Unkompliziert und sicher</p>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="text-center pt-4">
                <button
                  onClick={() => setShowWelcomePopup(false)}
                  className="bg-gradient-to-r from-orange-500 to-cyan-500 text-white px-8 py-3 rounded-full font-bold hover:from-orange-600 hover:to-cyan-600 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Los geht's! 🚀
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CookieBanner />
    </div>
  );
}
